import { createAsyncThunk, createSlice, PayloadAction, ActionReducerMapBuilder } from '@reduxjs/toolkit';
import axios from 'axios';
import API, { typeThrownException } from '@acorn-portal/shared/src/services/AxiosInstance';
import {
	TelematicsDriverTripsClient,
	TripDetailsDto,
} from '@acorn-portal/shared/src/services/telematicsDriverTripsApi';

interface TelematicsTripDetailsState {
	tripDetails: TripDetailsDto | null;
	loading: boolean;
	error: string | null;
}

const initialState: TelematicsTripDetailsState = {
	tripDetails: null,
	loading: false,
	error: null,
};

interface FetchTripDetailsParams {
	tripId: string;
	policyReference: string;
}

export const fetchTripDetails = createAsyncThunk<TripDetailsDto, FetchTripDetailsParams, { rejectValue: unknown }>(
	'telematicsTripDetails/fetchTripDetails',
	async (params: FetchTripDetailsParams, { rejectWithValue }) => {
		try {
			const client = new TelematicsDriverTripsClient(undefined, API);
			const response = await client.trips2(params.tripId, params.policyReference);
			return response;
		} catch (e) {
			if (axios.isAxiosError(e) && e.response) {
				if (e.response.status < 200 || e.response.status > 300) {
					return rejectWithValue(e);
				}
			}
			if (typeThrownException(e) && e.status) {
				if (e.status < 200 || e.status > 300) {
					return rejectWithValue(e);
				}
			}
			throw e;
		}
	},
);

export const telematicsTripDetailsSlice = createSlice({
	name: 'telematicsTripDetails',
	initialState,
	reducers: {
		setTripDetails: (state: TelematicsTripDetailsState, action: PayloadAction<TripDetailsDto>) => {
			state.tripDetails = action.payload;
		},
		clearTripDetails: () => initialState,
	},
	extraReducers: (builder: ActionReducerMapBuilder<TelematicsTripDetailsState>) => {
		builder.addCase(fetchTripDetails.pending, (state: TelematicsTripDetailsState) => {
			state.loading = true;
			state.error = null;
		});
		builder.addCase(fetchTripDetails.fulfilled, (state: TelematicsTripDetailsState, { payload }) => {
			state.loading = false;
			state.tripDetails = payload;
		});
		builder.addCase(fetchTripDetails.rejected, (state: TelematicsTripDetailsState, action) => {
			state.loading = false;
			state.error = action.error.message ?? 'Failed to fetch trip details';
		});
	},
});

export const { setTripDetails, clearTripDetails } = telematicsTripDetailsSlice.actions;
export default telematicsTripDetailsSlice.reducer;
